import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileSearch } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const monthYear = new Date().toLocaleString("default", { month: "short", year: "numeric" });

  return (
    <>
      {/* JSON-LD for Organization + Site Navigation (SEO Boost) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "RecruiterIQ",
          "url": "https://therecruiteriq.com",
          "logo": "https://therecruiteriq.com/images/triq-logo.png",
          "sameAs": [
            "https://www.linkedin.com/company/therecruiteriq/",
            "https://twitter.com/therecruiteriq"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "support@therecruiteriq.com",
            "contactType": "Customer Support"
          },
          "mainEntityOfPage": {
            "@type": "WebSite",
            "@id": "https://therecruiteriq.com"
          }
        })
      }} />
      
      <footer className="border-t bg-white mt-24" role="contentinfo">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/images/triq-logo.png" alt="TRIQ - The Recruiter IQ" className="w-8 h-8 rounded-full object-contain" />
                <h3 className="text-lg font-bold text-slate-900">RecruiterIQ</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                AI-Powered Strategic Hiring Intelligence Platform
              </p>
              <p className="text-xs text-slate-500">
                Empowering recruiters with intelligent tools and insights
              </p>
            </div>

            {/* Platform Section */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Platform</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => navigate('/about')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/contact')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Get In Touch
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/certifications')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors inline-flex items-center gap-1"
                  >
                    Certifications
                    <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Soon</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/awards')}
                    className="text-sm text-slate-600 hover:text-yellow-600 transition-colors inline-flex items-center gap-1"
                  >
                    Awards
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold">Live</span>
                  </button>
                </li>
                <li>
                  <button
                    data-testid="footer-extension-link"
                    onClick={() => navigate('/extension')}
                    className="text-sm text-slate-600 hover:text-cyan-600 transition-colors inline-flex items-center gap-1"
                  >
                    Browser Extension
                    <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded font-semibold">New</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Resources Section */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => navigate('/news')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Latest News
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/trends')}
                    className="text-sm text-slate-600 hover:text-purple-600 transition-colors"
                  >
                    Latest Trends
                  </button>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors inline-flex items-center gap-1"
                  >
                    Blog
                    <span className="text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-2 py-0.5 rounded font-semibold animate-pulse">NEW</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/library"
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors inline-flex items-center gap-1"
                  >
                    Library
                    <span className="text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-0.5 rounded font-semibold animate-pulse">NEW</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Section */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Legal & Policies</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/terms-conditions')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Terms & Conditions
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/ai-statement')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    A.I. Statement
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/accessibility')}
                    className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Accessibility
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Additional Footer Links Row */}
          <div className="flex flex-wrap items-center justify-center gap-4 py-4 mb-4 border-t border-b border-slate-200">
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
              aria-label="Open sitemap in new tab"
            >
              Sitemap
            </a>
            <span className="text-slate-300" aria-hidden="true">•</span>
            <button
              onClick={() => navigate('/careers')}
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
            >
              Careers
            </button>
            <span className="text-slate-300" aria-hidden="true">•</span>
            <button
              onClick={() => navigate('/contact')}
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
            >
              Help & Support
            </button>
            <span className="text-slate-300" aria-hidden="true">•</span>
            <span className="text-xs text-slate-500">
              Last Updated: <time dateTime={new Date().toISOString()}>{monthYear}</time>
            </span>
            <span className="text-slate-300" aria-hidden="true">•</span>
            <a
              href="/accessibility"
              className="text-xs text-slate-500 hover:text-blue-600 inline-flex items-center gap-1 transition-colors"
              aria-label="View accessibility statement"
            >
              <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              <span className="text-green-500">WCAG AA Compliant</span>
            </a>
          </div>

          {/* Divider */}
          <div className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Copyright */}
              <div className="flex flex-col md:flex-row items-center gap-4">
                <p className="text-sm text-slate-600">
                  © {new Date().getFullYear()} RecruiterIQ. All rights reserved.
                </p>
                <a
                  href="mailto:support@therecruiteriq.com"
                  className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  support@therecruiteriq.com
                </a>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/therecruiteriq/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow RecruiterIQ on LinkedIn"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0077B5] text-white rounded-lg hover:bg-[#006399] transition-colors text-xs font-medium"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  LinkedIn
                </a>
                <a
                  href="https://twitter.com/therecruiteriq"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow RecruiterIQ on X (formerly Twitter)"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  X
                </a>
              </div>
            </div>

            {/* Built By */}
            <div className="flex items-center justify-center gap-2 text-slate-700 mt-4 pt-4 border-t border-slate-100">
              <span className="text-sm">Built by</span>
              <a 
                href="https://www.linkedin.com/in/venkatjyothiswaroop/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-900 font-semibold hover:text-blue-600 transition-colors text-sm"
              >
                <span>Venkat J Swaroop</span>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
